import React from 'react';
import { ConfigProvider as AntdMobileConfigProvider } from 'antd-mobile';
import { useTranslation } from 'react-i18next';
import zhCN from 'antd-mobile/es/locales/zh-CN';
import enUS from 'antd-mobile/es/locales/en-US';
import esES from 'antd-mobile/es/locales/es-ES';

interface ConfigProviderProps {
  children: React.ReactNode;
}

/**
 * 全局配置提供者组件
 * 
 * 作用：
 * 1. 为所有 antd-mobile 组件提供语言包配置
 * 2. 根据当前 i18n 语言自动切换组件显示语言
 * 3. 统一管理整个应用的 antd-mobile 组件配置
 * 
 * 使用方式：
 * 在 App.tsx 中包裹整个应用，这样所有页面都能使用配置的语言包
 * 
 * 示例：
 * <ConfigProvider>
 *   <App />
 * </ConfigProvider>
 */
const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const { i18n, t } = useTranslation();

  /**
   * 根据当前语言获取对应的 antd-mobile locale 配置
   * 
   * 支持的语言：
   * - 中文 (zh/zh-CN): 显示中文界面文本
   * - 英文 (en/en-US): 显示英文界面文本  
   * - 西班牙语 (es/es-ES): 显示西班牙语界面文本
   * 
   * 影响范围：
   * - CalendarPicker 的月份名称、按钮文本
   * - DatePicker 的日期格式
   * - 其他 antd-mobile 组件的界面文本
   */
  const getLocale = () => {
    const currentLang = i18n.language;
    
    // 基础locale配置
    let baseLocale;
    switch (currentLang) {
      case 'zh':
      case 'zh-CN':
        baseLocale = zhCN;
        break;
      case 'es':
      case 'es-ES':
        baseLocale = esES;
        break;
      case 'en':
      case 'en-US':
      default:
        baseLocale = enUS;
        break;
    }

    // 为Calendar组件添加confirm字段的翻译
    // 使用翻译函数统一处理，而不是硬编码
    const enhancedLocale = {
      ...baseLocale,
      Calendar: {
        ...baseLocale.Calendar,
        // 使用翻译函数，保持与其他组件的一致性
        confirm: t('pages.delivery.delivered.confirm')
      }
    };

    return enhancedLocale;
  };

  return (
    <AntdMobileConfigProvider locale={getLocale()}>
      {children}
    </AntdMobileConfigProvider>
  );
};

export default ConfigProvider;
